import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { X, Loader2, CheckCircle2, AlertCircle, MessageCircle, Phone, Hourglass } from 'lucide-react';
import toast from 'react-hot-toast';
import { useRequestP2pOrderMutation, useGetMyPendingOrdersQuery } from '../../api/apiSlice';
import { computePricing, formatKzt } from '../../lib/coursePricing';

// "5 мин / 2 ч / 3 д назад" — короткое человеческое время.
function formatAgo(iso) {
  if (!iso) return '';
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return 'только что';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} мин назад`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} ч назад`;
  const d = Math.floor(h / 24);
  return `${d} д назад`;
}

/**
 * P2P Payment Request Modal — простая форма заявки на покупку курса.
 *
 * Студент оставляет:
 *   • имя (из профиля, можно править)
 *   • телефон + чекбокс WhatsApp
 *   • опц. комментарий
 *
 * → создаётся Order(status=pending_contact) → админ свяжется вручную.
 *
 * Это НЕ интегрированная оплата. Деньги принимает админ офлайн через Kaspi P2P.
 */
export default function PaymentRequestModal({ isOpen, course, onClose, onSuccess }) {
  const { user } = useSelector((state) => state.auth);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('+7 ');
  const [hasWhatsapp, setHasWhatsapp] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  // Студент должен явно подтвердить, что ознакомился с офертой, политикой
  // возврата и пользовательским соглашением. Без галочки кнопка disabled —
  // это юридически защищает обе стороны и убирает «я не читал» аргумент.
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const [requestOrder, { isLoading, error }] = useRequestP2pOrderMutation();
  // Подтягиваем список ожидающих заявок юзера. Если на ЭТОТ курс уже есть
  // — показываем "вы уже подали заявку" вместо формы.
  // Запрос пропускаем когда модалка закрыта, чтобы не дёргать бэк зря.
  const { data: pendingData } = useGetMyPendingOrdersQuery(undefined, { skip: !isOpen });
  const existingOrder = useMemo(() => {
    if (!isOpen || !course || !pendingData?.orders) return null;
    const cid = course._id || course.id;
    return pendingData.orders.find(
      (o) => (o.course?.id === cid || o.courseId === cid) && o.status === 'pending_contact'
    );
  }, [isOpen, course, pendingData]);

  useEffect(() => {
    if (isOpen) {
      setName(user?.name || '');
      setPhone('+7 ');
      setHasWhatsapp(true);
      setSubmitted(false);
      setAcceptedTerms(false);
    }
  }, [isOpen, user]);

  // Нормализуем ввод телефона: всегда начинается с "+7 ", дальше только цифры.
  // Прикрывает кейсы: вставка из буфера "8 777...", "+7777...", "777..." и т.п.
  const handlePhoneChange = (e) => {
    const raw = e.target.value.replace(/[^\d+]/g, '');
    let digits = raw.startsWith('+') ? raw.slice(1) : raw;
    // 8XXX → 7XXX (казахстанский префикс)
    if (digits.startsWith('8')) digits = '7' + digits.slice(1);
    // Если не начинается с 7 — префиксуем
    if (!digits.startsWith('7')) digits = '7' + digits;
    // Ограничиваем 11 цифр (7 + 10)
    digits = digits.slice(0, 11);
    setPhone(digits.length > 1 ? `+${digits}` : '+7 ');
  };

  if (!isOpen || !course) return null;

  const { finalPrice } = computePricing(course);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanPhone = phone.replace(/[\s\-()]/g, '');
    if (!/^\+?\d{10,15}$/.test(cleanPhone)) {
      toast.error('Введите корректный номер телефона');
      return;
    }
    try {
      await requestOrder({
        courseId: course._id || course.id,
        phone: cleanPhone,
        hasWhatsapp,
      }).unwrap();
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.data?.message || 'Не удалось отправить заявку');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {existingOrder
                ? 'Вы уже подали заявку'
                : submitted
                ? 'Заявка отправлена'
                : 'Заявка на курс'}
            </h2>
            {!submitted && !existingOrder && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">
                {course.title}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 -mr-2 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Закрыть"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        {existingOrder ? (
          // Юзер уже подавал заявку на этот курс — показываем статус
          // вместо формы. Защита от случайной повторной отправки.
          <div className="p-6 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/40">
              <Hourglass className="h-8 w-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white break-words">
              {course.title}
            </h3>
            <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">
              Заявка уже отправлена <b>{formatAgo(existingOrder.createdAt)}</b>.
              {' '}С вами свяжутся в течение <b>24 часов</b> для оплаты — повторно отправлять не нужно.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-5 w-full rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 text-sm"
            >
              Понятно
            </button>
          </div>
        ) : submitted ? (
          <div className="p-6 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/40">
              <CheckCircle2 className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              Заявка отправлена!
            </h3>
            <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">
              С вами свяжутся в течение <b>24 часов</b> через
              {hasWhatsapp ? ' WhatsApp' : ' звонок'} для оплаты.
            </p>
            <button
              type="button"
              onClick={() => {
                onSuccess?.();
                onClose();
              }}
              className="mt-5 w-full rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 text-sm"
            >
              Понятно
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {/* Price */}
            <div className="rounded-2xl bg-indigo-50 dark:bg-indigo-950/30 px-4 py-3 flex items-center justify-between">
              <span className="text-sm text-indigo-900 dark:text-indigo-200">К оплате</span>
              <span className="text-xl font-bold tracking-tight text-indigo-700 dark:text-indigo-300 tabular-nums">
                {formatKzt(finalPrice)}
              </span>
            </div>

            {/* Name */}
            <label className="block">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                Ваше имя
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Айдана Абдыкадырова"
                className="mt-1.5 w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none"
              />
            </label>

            {/* Phone */}
            <label className="block">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                Телефон для связи *
              </span>
              <div className="mt-1.5 relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="tel"
                  inputMode="tel"
                  required
                  value={phone}
                  onChange={handlePhoneChange}
                  placeholder="+7 707 123 45 67"
                  className="w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 pl-10 pr-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none tabular-nums"
                />
              </div>
            </label>

            {/* WhatsApp */}
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hasWhatsapp}
                onChange={(e) => setHasWhatsapp(e.target.checked)}
                className="mt-0.5 h-5 w-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              <div className="flex-1">
                <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                  <MessageCircle className="h-4 w-4 text-emerald-600" />
                  На этом номере есть WhatsApp
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Рекомендуем — связь будет быстрее
                </p>
              </div>
            </label>

            {error && (
              <div className="flex items-start gap-2 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error?.data?.message || 'Ошибка отправки'}</span>
              </div>
            )}

            {/* Согласие — пока не отмечено, кнопка disabled.
                Это не только UX-приём, но и юридическая фиксация: студент явным
                действием подтверждает что прочитал документы. _blank чтобы
                переход не закрывал модалку. */}
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5 h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 shrink-0"
              />
              <div className="text-xs leading-relaxed text-gray-600 dark:text-gray-400">
                Я ознакомился(лась) и согласен(на) с{' '}
                <a
                  href="/legal/offer"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                >
                  публичной офертой
                </a>
                ,{' '}
                <a
                  href="/legal/refund"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                >
                  политикой возврата
                </a>
                {' '}и{' '}
                <a
                  href="/legal/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                >
                  пользовательским соглашением
                </a>
                .
              </div>
            </label>

            <button
              type="submit"
              disabled={isLoading || !acceptedTerms}
              className="w-full rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:scale-[0.98] transition"
            >
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {isLoading ? 'Отправляем...' : 'Отправить заявку'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
